// src/utils/generateHRManual.js
// Generates the full PrrepX Pathshala HR Policy Manual as a professional PDF.
// Uses jsPDF (already installed). No html2canvas needed — pure vector text/shapes.

import { jsPDF } from 'jspdf';
import { holidayTables } from '../config/holidays';

// ─────────────────────────────────────────────────────────────────────────────
// COLOURS & LAYOUT CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────
const C = {
  brand:      [0,   64,  128],   // #004080
  brandMid:   [0,   96,  200],   // #0060c8
  brandLight: [99,  163, 255],   // #63a3ff
  accent:     [234, 179, 8],     // amber
  dark:       [15,  23,  42],    // #0f172a
  mid:        [71,  85,  105],   // #475569
  muted:      [148, 163, 184],   // #94a3b8
  light:      [241, 245, 249],   // #f1f5f9
  white:      [255, 255, 255],
  green:      [22,  163, 74],
  red:        [220, 38,  38],
  bannerBg:   [226, 232, 240],   // section banner bg
};

const MARGIN  = 18;   // mm left/right
const PW      = 210;  // page width mm (A4)
const PH      = 297;  // page height mm (A4)
const CW      = PW - MARGIN * 2; // content width
const FOOTER_H = 14;  // footer height
const HEADER_H = 0;   // for non-cover pages (we draw manually)

// ─────────────────────────────────────────────────────────────────────────────
// RENDERER CLASS — wraps jsPDF and tracks cursor position
// ─────────────────────────────────────────────────────────────────────────────
class Renderer {
  constructor(doc) {
    this.doc = doc;
    this.y   = MARGIN;
    this.page = 1;
    this.toc  = []; // { title, page }
    this.tocPage = 2;
  }

  // ── utilities ──────────────────────────────────────────────────────────────
  get pageBottom() { return PH - FOOTER_H - 6; }

  needsNewPage(height = 10) {
    if (this.y + height > this.pageBottom) {
      this.addPage();
      return true;
    }
    return false;
  }

  addPage() {
    this.doc.addPage();
    this.page++;
    this.y = MARGIN + 10;
    this._drawPageFooter();
    this._drawPageHeader();
  }

  _drawPageFooter() {
    const doc = this.doc;
    // footer line
    doc.setDrawColor(...C.bannerBg);
    doc.setLineWidth(0.4);
    doc.line(MARGIN, PH - FOOTER_H, PW - MARGIN, PH - FOOTER_H);
    // footer text
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...C.muted);
    doc.text('PrrepX Pathshala Pvt. Ltd. · HR Policy Manual · Confidential — Internal Use Only', MARGIN, PH - 8);
    doc.text(`Page ${this.page}`, PW - MARGIN, PH - 8, { align: 'right' });
  }

  _drawPageHeader() {
    const doc = this.doc;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...C.muted);
    doc.text('PrrepX Pathshala Pvt. Ltd.', MARGIN, MARGIN + 2);
    doc.text('HR Policy Manual', PW - MARGIN, MARGIN + 2, { align: 'right' });
    doc.setDrawColor(...C.bannerBg);
    doc.setLineWidth(0.4);
    doc.line(MARGIN, MARGIN + 4, PW - MARGIN, MARGIN + 4);
    this.y = MARGIN + 10;
  }

  // ── text helpers ───────────────────────────────────────────────────────────
  setColor(rgb) { this.doc.setTextColor(...rgb); return this; }
  setFill(rgb)  { this.doc.setFillColor(...rgb); return this; }

  text(str, opts = {}) {
    const {
      x       = MARGIN,
      size    = 10,
      font    = 'normal',
      color   = C.dark,
      align   = 'left',
      maxW    = CW,
      indent  = 0,
      lineH   = 5.5,
    } = opts;
    const doc = this.doc;
    doc.setFont('helvetica', font);
    doc.setFontSize(size);
    doc.setTextColor(...color);
    const lines = doc.splitTextToSize(str, maxW - indent);
    lines.forEach((line, i) => {
      this.needsNewPage(lineH + 2);
      doc.text(line, x + indent + (align === 'center' ? (maxW / 2) : 0), this.y, { align });
      this.y += lineH;
    });
    return this;
  }

  br(mm = 4) { this.y += mm; return this; }

  // ── structural helpers ──────────────────────────────────────────────────────
  chapterBanner(num, title) {
    this.needsNewPage(22);
    const doc = this.doc;
    // full-width coloured band
    doc.setFillColor(...C.brand);
    doc.rect(0, this.y - 2, PW, 18, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(...C.white);
    doc.text(`Chapter ${num}  ·  ${title}`, MARGIN, this.y + 10);
    this.y += 20;
    // record for TOC
    this.toc.push({ num, title, page: this.page });
    return this;
  }

  sectionTitle(title) {
    this.needsNewPage(14);
    const doc = this.doc;
    doc.setFillColor(...C.light);
    doc.rect(MARGIN - 2, this.y - 2, CW + 4, 10, 'F');
    doc.setDrawColor(...C.brandLight);
    doc.setLineWidth(2.5);
    doc.line(MARGIN - 2, this.y - 2, MARGIN - 2, this.y + 8);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10.5);
    doc.setTextColor(...C.brand);
    doc.text(title, MARGIN + 4, this.y + 5.5);
    this.y += 14;
    doc.setLineWidth(0.4);
    return this;
  }

  bullet(text, opts = {}) {
    const { indent = 6, color = C.mid, size = 9.5, subBullet = false } = opts;
    this.needsNewPage(7);
    const doc  = this.doc;
    const bx   = MARGIN + indent;
    const cx   = MARGIN + indent + (subBullet ? 8 : 6);
    const maxW = CW - indent - (subBullet ? 8 : 6);
    doc.setFillColor(...(subBullet ? C.muted : C.brandMid));
    doc.circle(bx, this.y - 1.2, subBullet ? 0.8 : 1.2, 'F');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(size);
    doc.setTextColor(...color);
    const lines = doc.splitTextToSize(text, maxW);
    lines.forEach((line, i) => {
      if (i > 0) this.needsNewPage(5);
      doc.text(line, cx, this.y);
      this.y += 5;
    });
    this.y += 1;
    return this;
  }

  definition(term, desc, opts = {}) {
    this.needsNewPage(12);
    const doc = this.doc;
    const { indent = 6 } = opts;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.setTextColor(...C.brand);
    doc.text(term, MARGIN + indent, this.y);
    this.y += 5;
    const lines = doc.splitTextToSize(desc, CW - indent - 4);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...C.mid);
    doc.setFontSize(9.5);
    lines.forEach(line => {
      this.needsNewPage(5);
      doc.text(line, MARGIN + indent + 4, this.y);
      this.y += 5;
    });
    this.y += 2;
    return this;
  }

  // A 2-column labelled row
  row(label, value, opts = {}) {
    this.needsNewPage(8);
    const doc = this.doc;
    const { colW = 58 } = opts;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...C.mid);
    doc.text(label, MARGIN + 6, this.y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...C.dark);
    const lines = doc.splitTextToSize(value, CW - colW - 10);
    lines.forEach((ln, i) => {
      if (i > 0) this.needsNewPage(5);
      doc.text(ln, MARGIN + colW, this.y);
      this.y += 5;
    });
    if (lines.length === 1) this.y += 1; // small gap
    return this;
  }

  hRule(color = C.bannerBg) {
    this.doc.setDrawColor(...color);
    this.doc.setLineWidth(0.3);
    this.doc.line(MARGIN, this.y, PW - MARGIN, this.y);
    this.y += 4;
    return this;
  }

  infoBox(text, opts = {}) {
    const { bgColor = [239, 246, 255], borderColor = C.brandLight, iconPrefix = 'NOTE:  ' } = opts;
    const doc = this.doc;
    const lines = doc.splitTextToSize(iconPrefix + text, CW - 12);
    const boxH = lines.length * 5 + 8;
    this.needsNewPage(boxH);
    doc.setFillColor(...bgColor);
    doc.setDrawColor(...borderColor);
    doc.setLineWidth(0.5);
    doc.roundedRect(MARGIN, this.y, CW, boxH, 2, 2, 'FD');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...C.brand);
    lines.forEach((line, i) => {
      doc.text(line, MARGIN + 6, this.y + 6 + i * 5);
    });
    this.y += boxH + 4;
    return this;
  }

  legalNote(text) {
    return this.infoBox(text, {
      bgColor:     [254, 252, 232],
      borderColor: [234, 179, 8],
      iconPrefix:  'LAW:   ',
    });
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// COVER PAGE
// ─────────────────────────────────────────────────────────────────────────────
function drawCover(doc) {
  // gradient-like top band
  doc.setFillColor(...C.brand);
  doc.rect(0, 0, PW, 110, 'F');

  doc.setFillColor(...C.brandMid);
  doc.rect(0, 95, PW, 20, 'F');

  // decorative circles
  doc.setFillColor(255, 255, 255);
  doc.setGState(new doc.GState({ opacity: 0.04 }));
  doc.circle(170, 30, 60, 'F');
  doc.circle(20, 80, 40, 'F');
  doc.setGState(new doc.GState({ opacity: 1 }));

  // company name
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(...C.white);
  doc.text('PrrepX Pathshala', PW / 2, 42, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(13);
  doc.setTextColor(200, 220, 255);
  doc.text('Private Limited', PW / 2, 52, { align: 'center' });

  // thin divider line
  doc.setDrawColor(255, 255, 255);
  doc.setLineWidth(0.4);
  doc.line(MARGIN + 30, 59, PW - MARGIN - 30, 59);

  // document title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(28);
  doc.setTextColor(...C.white);
  doc.text('HR Policy Manual', PW / 2, 74, { align: 'center' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(12);
  doc.setTextColor(180, 210, 255);
  doc.text('Employee Handbook — All Staff', PW / 2, 84, { align: 'center' });

  // white body area
  doc.setFillColor(...C.white);
  doc.rect(0, 115, PW, PH - 115, 'F');

  // metadata box
  const bx = MARGIN + 10, by = 128, bw = CW - 20, bh = 70;
  doc.setFillColor(...C.light);
  doc.setDrawColor(...C.bannerBg);
  doc.setLineWidth(0.5);
  doc.roundedRect(bx, by, bw, bh, 3, 3, 'FD');

  const rows = [
    ['Document Type',  'HR Policy Manual / Employee Handbook'],
    ['Applicability',  'All Employees — Regular, Full-Time'],
    ['Effective Date', new Date().toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', year: 'numeric', month: 'long', day: 'numeric' })],
    ['Version',        'Version 1.0 — 2026 Edition'],
    ['Issued By',      'Human Resources — PrrepX Pathshala Pvt. Ltd.'],
    ['Classification', 'Confidential · Internal Use Only'],
  ];

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.5);
  doc.setTextColor(...C.mid);
  rows.forEach(([label, val], i) => {
    const ry = by + 9 + i * 10;
    doc.text(label, bx + 8, ry);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...C.dark);
    doc.text(val, bx + 58, ry);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...C.mid);
  });

  // address
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(...C.muted);
  doc.text('ET 15/302 EWS LIG, DLF Garden, Mohanlalganj, Lucknow, Uttar Pradesh – 226301', PW / 2, 215, { align: 'center' });
  doc.text('admin@prrepxpathshala.live  ·  +91 9266517468  ·  prrepxpathshala.live', PW / 2, 222, { align: 'center' });
  doc.text('CIN: U85306UP2025PTC238181', PW / 2, 229, { align: 'center' });

  // disclaimer
  doc.setFillColor(254, 242, 242);
  doc.setDrawColor(252, 165, 165);
  doc.setLineWidth(0.4);
  doc.roundedRect(MARGIN + 10, 238, CW - 20, 22, 2, 2, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(153, 27, 27);
  doc.text('CONFIDENTIAL', PW / 2, 246, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(185, 28, 28);
  doc.text('This document is the property of PrrepX Pathshala Pvt. Ltd. and is intended for employees only.', PW / 2, 252, { align: 'center' });
  doc.text('Unauthorised reproduction, distribution, or disclosure is strictly prohibited.', PW / 2, 257, { align: 'center' });

  // footer
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...C.muted);
  doc.text('© 2026 PrrepX Pathshala Pvt. Ltd. All rights reserved.', PW / 2, PH - 8, { align: 'center' });
}

// ─────────────────────────────────────────────────────────────────────────────
// TABLE OF CONTENTS  (rendered after all chapters are built)
// ─────────────────────────────────────────────────────────────────────────────
function drawTOC(doc, toc, tocPage) {
  // We already reserved page 2 — we'll add content directly via absolute positioning
  // In practice we insert pages at the end and won't try to reorder.
  // Instead, the TOC is built on page 2 using the page references collected
  // during chapter rendering. Because jsPDF doesn't support page insertion,
  // we render chapters first (pages 3→N) and then write the TOC on page 2.
  // We achieve this by calling doc.setPage(tocPage) after all chapters are done.
  doc.setPage(tocPage);

  // header band
  doc.setFillColor(...C.brand);
  doc.rect(0, 0, PW, 36, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(...C.white);
  doc.text('Table of Contents', PW / 2, 22, { align: 'center' });

  let y = 48;

  toc.forEach((item, i) => {
    const isEven = i % 2 === 0;
    if (isEven) {
      doc.setFillColor(...C.light);
      doc.rect(MARGIN - 2, y - 5, CW + 4, 11, 'F');
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.setTextColor(...C.brand);
    doc.text(`Ch. ${item.num}`, MARGIN + 2, y + 1);

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...C.dark);
    doc.text(item.title, MARGIN + 18, y + 1);

    // dots
    const titleW = doc.getTextWidth(item.title);
    const pageNumStr = `${item.page}`;
    const pageNumW   = doc.getTextWidth(pageNumStr) + 4;
    const dotsStart  = MARGIN + 18 + titleW + 3;
    const dotsEnd    = PW - MARGIN - pageNumW - 4;
    doc.setTextColor(...C.muted);
    doc.setFontSize(8);
    let dotX = dotsStart;
    while (dotX < dotsEnd) {
      doc.text('.', dotX, y + 1);
      dotX += 2.8;
    }

    // page number
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.setTextColor(...C.brandMid);
    doc.text(pageNumStr, PW - MARGIN - 2, y + 1, { align: 'right' });

    y += 11;
  });

  // footer
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...C.muted);
  doc.text('PrrepX Pathshala Pvt. Ltd. · HR Policy Manual · Confidential — Internal Use Only', MARGIN, PH - 8);
  doc.text(`Page ${tocPage}`, PW - MARGIN, PH - 8, { align: 'right' });
}

// ─────────────────────────────────────────────────────────────────────────────
// CHAPTER CONTENT FUNCTIONS
// ─────────────────────────────────────────────────────────────────────────────

function ch1_About(r) {
  r.chapterBanner(1, 'About PrrepX Pathshala').br(4);

  r.sectionTitle('1.1  Who We Are');
  r.text("PrrepX Pathshala is India's first EdTech with 100% faculties from IIT/NIT & Medical alumni. We have brought together the best academic minds at one place, creating a purely energetic and purpose-driven learning atmosphere that is unmatched in the industry.", { size: 9.5, color: C.mid });
  r.br(3);
  r.text("We are a one-stop solution for JEE, NEET, and Class 6-12th students — delivering video lectures, comprehensive chapter notes, PYQ solutions, and full-length test series, all crafted by alumni who have themselves cracked the examinations our students aspire to.", { size: 9.5, color: C.mid });
  r.br(3);
  r.text('We operate multiple digital platforms:', { size: 9.5, color: C.mid });
  r.bullet('Main Educational Portal -- prrepxpathshala.live');
  r.bullet('Test Series Platform -- testseries.prrepxpathshala.live');
  r.bullet('Employee Management System -- employee.prrepxpathshala.live');
  r.br();

  r.sectionTitle('1.2  Our Mission');
  r.text('To build the best-in-market learning content and deliver it free or at affordable cost, empowering students from any economic background to compete on equal footing in national examinations.', { size: 9.5, color: C.mid });
  r.br();

  r.sectionTitle('1.3  Company Details');
  r.row('Registered Name',   'PrrepX Pathshala Private Limited');
  r.row('CIN',               'U85306UP2025PTC238181');
  r.row('Registered Office', 'ET 15/302 EWS LIG, DLF Garden, Mohanlalganj, Lucknow, Uttar Pradesh – 226301');
  r.row('Email',             'admin@prrepxpathshala.live');
  r.row('Phone',             '+91 9266517468');
  r.row('Website',           'prrepxpathshala.live');
  r.br();

  r.sectionTitle('1.4  Scope of This Manual');
  r.text('This HR Policy Manual applies to all employees of PrrepX Pathshala Pvt. Ltd. regardless of designation or department. It sets out the policies, entitlements, responsibilities, and conduct standards applicable throughout your employment.', { size: 9.5, color: C.mid });
  r.br();
  r.infoBox('This manual is a living document. HR reserves the right to amend, update, or supplement it at any time. Changes will be communicated via the Employee Portal. Employees are responsible for staying updated.');

  r.sectionTitle('1.5  Equal Opportunity Employer');
  r.text('PrrepX Pathshala Private Limited is an equal opportunity employer. Recruitment, hiring, and terms of employment are determined solely on the basis of merit, qualifications, and business need, without regard to gender, religion, caste, race, disability, marital status, or any other characteristic protected under applicable Indian law.', { size: 9.5, color: C.mid });
}

function ch2_Employment(r) {
  r.chapterBanner(2, 'Employment & Onboarding').br(4);

  r.sectionTitle('2.1  Types of Employment');
  r.definition('Full-Time Regular Employment', 'The standard mode of engagement. Employees work on a full-time, on-site basis as specified in the Offer Letter. All policies in this manual apply in full to regular employees.');
  r.definition('Probationary / Introductory Period', 'The first 3 (Three) calendar months from the Date of Joining constitute the Probation Period. This is a mandatory assessment phase during which your performance, conduct, attitude, attendance, and adherence to all Company policies are closely monitored. Continuation beyond this period is subject to satisfactory performance and a formal written confirmation letter from the Company. There is no implied confirmation from silence or continued employment — only a written confirmation letter is valid. The Company may extend the probation period by up to 3 additional months if performance warrants further review.');
  r.br();

  r.sectionTitle('2.2  Offer Letter & Employment Contract');
  r.text('Every employee receives a formal Offer Letter before joining. The Offer Letter is the primary employment contract and contains:', { size: 9.5, color: C.mid });
  r.bullet('Designation, Department, Grade / Level, and Reporting Structure');
  r.bullet('Date of Joining and Primary Work Location');
  r.bullet('Full CTC (Cost to Company) and detailed Salary Structure');
  r.bullet('Variable Pay, Retention Bonus, and Joining / Relocation Bonus terms');
  r.bullet('Bond Clause and financial obligations on early exit');
  r.bullet('Confidentiality and Intellectual Property obligations');
  r.br();
  r.legalNote('The Offer Letter constitutes a legally binding agreement. Employees must read it carefully before signing. Any questions should be raised with HR before the date of joining.');
  r.br();

  r.sectionTitle('2.3  Onboarding Checklist');
  r.text('On your first day, you are required to:', { size: 9.5, color: C.mid });
  r.bullet('Sign and return your Offer Letter');
  r.bullet('Submit all required joining documents (see Chapter 8)');
  r.bullet('Log in to the Employee Portal and verify your profile details');
  r.bullet('Receive and save the Dedicated Content Drive link from your admin');
  r.bullet('Confirm your first task assignment with your reporting manager');
  r.bullet('Review this HR Manual and acknowledge you have read it');
  r.br();

  r.sectionTitle('2.4  Employee ID');
  r.text('Upon joining, a unique Employee ID is assigned to you. This ID is used on all official communications, task tickets, payslips, and HR documentation. You can find it in My Portal > My Profile. Always quote your Employee ID in any HR correspondence.', { size: 9.5, color: C.mid });
  r.br();

  r.sectionTitle('2.5  Bond Clause');
  r.text('The Offer Letter includes a financial service bond (2-year minimum service period). If you exit early, a Bond Breach compensation (Training & Content Indemnity) becomes due — recovery of PrrepX Pathshala’s investment in your proprietary training materials, content access, onboarding, and replacement recruitment. Maximum = 3 months’ Gross Monthly Salary (Basic + HRA + LTA + Medical + Telephone + Special, as fixed in your Offer Letter — excludes Variable Pay, Retention Bonus, and CTC). Crucially, this amount reduces pro-rata: Payable = Maximum × (Months Remaining in Bond Period ÷ 24). Example: exit after 18 months → only 25% of maximum is due. Please review your own Offer Letter for the exact maximum amount applicable to you.', { size: 9.5, color: C.mid });
  r.br();
  r.legalNote('The bond is enforceable under applicable Indian contract law. If you exit within the bond period, the company reserves the right to recover the stipulated amount from your Full & Final Settlement or through appropriate legal means.');
  r.br();

  r.sectionTitle('2.6  Leave During Probation Period');
  r.text('The Probation Period (first 3 calendar months from Date of Joining) is a critical onboarding and evaluation phase. The following leave restrictions apply strictly during this period:', { size: 9.5, color: C.mid });
  r.bullet('No leave of any kind is available or permissible during the 3-month Probation Period — this includes Earned Leave (EL), Casual Leave (CL), Sick Leave (SL), and Unpaid Leave (UL). Employees do not have the right to apply for any leave — paid or unpaid — on their own during probation.');
  r.bullet('The sole exception is a genuine emergency situation, which requires prior written or verbal approval from the reporting manager or HR before the absence.');
  r.bullet('All leave entitlements (EL, CL, SL, etc.) begin to accrue only from the date of successful completion of the 3-month Probation Period. No retroactive credit is given for the probation months.');
  r.bullet('Any emergency leave availed during probation will be recorded and treated as Leave Without Pay (LWP) at the Company\'s discretion.');
  r.bullet('Leaves during probation do not carry forward and are not adjustable against any future leave balance.');
  r.bullet('Repeated or unexplained absences during probation may result in extension of the probation period or termination of employment.');
  r.br();
  r.infoBox('Regular attendance during probation is a non-negotiable expectation. The probation period exists precisely so the Company can evaluate your reliability and commitment. Unauthorised absence during this phase adversely affects your confirmation.');
  r.br();

  r.sectionTitle('2.7  Pre-Joining Breach — Irrevocability of Accepted Offer');
  r.text('Once you sign and return the Offer Letter (physically, by scanned copy, or via electronic transmission), your acceptance is legally binding and irrevocable under the Indian Contract Act, 1872. A contract comes into effect from the moment your acceptance is dispatched or communicated to the Company.', { size: 9.5, color: C.mid });
  r.br(2);
  r.text('As per Company policy and HR workflow, this process cannot be reversed or delayed by the employee. The following actions after returning a signed Offer Letter constitute a Pre-Joining Breach:', { size: 9.5, color: C.mid });
  r.bullet('Failing to report for duty on the agreed Date of Joining without prior written Company approval');
  r.bullet('Requesting a postponement or delay of the Date of Joining without the Company\'s express written consent');
  r.bullet('Withdrawing, revoking, or attempting to cancel acceptance of the offer');
  r.bullet('Failing to complete mandatory pre-joining formalities within stipulated timelines');
  r.bullet('Joining a competitor organisation or commencing alternate employment after accepting this offer');
  r.br();
  r.text('As no training, content access, or on-the-job investment has yet been made in a candidate who has not joined, a Pre-Joining Breach does NOT attract the Bond Breach (Training & Content Indemnity) formula. Instead, the Company may recover its genuine pre-estimate of actual costs incurred up to that point (e.g., background verification already initiated, seat/resource reservation, administrative/recruitment costs of re-advertising the role), subject to the maximum cap specified in the employee\'s Offer Letter (typically ₹15,000). This amount is independent of, and not combined with, any Bond Breach compensation, which applies only after joining.', { size: 9.5, color: C.mid });
  r.br();
  r.legalNote('This clause is enforceable as liquidated damages under Section 74 of the Indian Contract Act, 1872. While Section 14(1)(b) of the Specific Relief Act, 1963 precludes a court from ordering specific performance of a personal service contract (i.e., no court can physically compel joining), the Company\'s right to recover its genuine, documented pre-joining costs is unaffected and enforceable. Employees should accept the offer only upon genuine commitment to join.');
  r.br();
  r.text('Note on Company\'s Rights: The Company reserves the exclusive right to postpone, reschedule, or advance the Date of Joining for any business, operational, or administrative reason. Such rescheduling by the Company does not entitle the employee to any compensation and does not affect the employee\'s binding obligation under the contract. This asymmetry is intentional — the Company bears organisational planning responsibilities and may adjust timelines for legitimate business reasons, while the employee\'s commitment, once given, is binding.', { size: 9.5, color: C.mid });
}

function ch3_Attendance(r) {
  r.chapterBanner(3, 'Working Hours, Attendance & Punctuality').br(4);

  r.sectionTitle('3.1  Official Work Hours');
  r.row('Working Days',    'Monday through Saturday (6 days a week)');
  r.row('Check-In Time',   '9:30 AM (strict)');
  r.row('Check-Out Time',  '6:30 PM (strict)');
  r.row('Lunch Break',     '1 hour (between 1:00 PM – 2:30 PM)');
  r.row('Net Work Hours',  '8 hours per working day');
  r.row('Sunday',          'Fixed weekly off for all employees — Sunday is never a working day.');
  r.br();
  r.legalNote('These hours are compliant with applicable working hour limits under the Uttar Pradesh Shops and Commercial Establishments Act. Overtime (beyond 8 net hours) is voluntary, tracked, and credited towards performance and increments.');
  r.br();

  r.sectionTitle('3.2  Attendance Marking — How It Works');
  r.text('Attendance must be marked through the Employee Portal (My Portal > Attendance) every working day:', { size: 9.5, color: C.mid });
  r.bullet('Check In: Click the "Check In" button at or before 9:30 AM each day.');
  r.bullet('Check Out: Click the "Check Out" button at 6:30 PM each day.');
  r.bullet('Monthly history and status (Present / Absent / Half Day) is visible on the Attendance calendar.');
  r.br();

  r.sectionTitle('3.3  Missed or Incorrect Attendance');
  r.text('If you forget to check in or check out, report it to admin via My Portal > Chat with Admin on the same day with your actual times. Do not let corrections accumulate across days.', { size: 9.5, color: C.mid });
  r.br();
  r.infoBox('Repeated missed markings are flagged during performance reviews. Make attendance marking the first and last action of every working day — set phone reminders if needed.');
  r.br();

  r.sectionTitle('3.4  Punctuality Policy');
  r.text('Punctuality is a non-negotiable standard. Habitual late check-ins or early check-outs are tracked and negatively impact your Attendance KRA rating during quarterly performance reviews.', { size: 9.5, color: C.mid });
  r.br();

  r.sectionTitle('3.5  Extra Hours');
  r.text('Extra work beyond 6:30 PM is voluntary but encouraged. To log extra hours, add a note in your daily task update (My Tasks) with exact times before logging off. Extra hours are considered during increment and performance calculations.', { size: 9.5, color: C.mid });
  r.br();

  r.sectionTitle('3.6  Work Location');
  r.text('The primary work location and work mode (on-site / remote) are specified in your Offer Letter. Temporary work-from-home (WFH) arrangements require explicit prior approval from admin via Chat. Ad-hoc, unapproved WFH is not permitted and will be treated as absence.', { size: 9.5, color: C.mid });
}

function ch4_Leave(r) {
  r.chapterBanner(4, 'Leave Policy').br(4);

  r.sectionTitle('4.0  Leave During Probation Period — Important Restriction');
  r.infoBox('PROBATION RESTRICTION: No leave of any kind may be availed during the first 3 (Three) calendar months of employment (the Probation Period). All leave entitlements listed in this chapter begin to accrue ONLY from the date of successful completion of probation. Read Section 4.0 carefully before applying for any leave.', { bgColor: [254, 242, 242], borderColor: [220, 38, 38], iconPrefix: 'ALERT: ' });
  r.br(2);
  r.text('The Probation Period is the first 3 (Three) calendar months from an employee\'s Date of Joining. This period is a mandatory assessment and onboarding phase. The following leave rules apply strictly during probation:', { size: 9.5, color: C.mid });
  r.bullet('No Earned Leave (EL), Casual Leave (CL), Sick Leave (SL), or Unpaid Leave (UL) can be applied for or availed during probation. Employees do not hold the right to apply for any leave type — including UL — on their own during the Probation Period.');
  r.bullet('The sole exception is a genuine emergency — which must be communicated to the reporting manager or HR immediately, and approval must be obtained before or on the day of absence.');
  r.bullet('Emergency leave availed during probation is recorded as Leave Without Pay (LWP) at the Company\'s discretion. It does not reduce any future leave balance (since no balance has accrued yet).');
  r.bullet('Leave entitlements begin accruing from the first day after probation ends. There is no retroactive accrual for the probation months.');
  r.bullet('Repeated or unexplained absences during probation are treated as a serious performance issue and may lead to probation extension or termination.');
  r.legalNote('This probation leave restriction does not override statutory rights. If an employee has a compulsory statutory right that applies from Day 1 (e.g., Maternity Leave under the Maternity Benefit Act, 2017, for eligible employees), those rights are unaffected. For all other leave types, the 3-month restriction applies.');
  r.br();

  r.sectionTitle('4.1  Governing Legislation');
  r.text('The leave policy of PrrepX Pathshala is aligned with applicable Indian labour legislation including:', { size: 9.5, color: C.mid });
  r.bullet('Uttar Pradesh Shops & Commercial Establishments Act (for EL / PL entitlements)');
  r.bullet('Maternity Benefit (Amendment) Act, 2017 (for Maternity Leave)');
  r.bullet('Payment of Wages Act and applicable state rules');
  r.legalNote('Statutory minimum entitlements provided by law cannot be reduced by any company policy. Where this policy grants more than the statutory minimum, the company policy prevails. Where law mandates more, law prevails.');
  r.br();

  r.sectionTitle('4.2  How to Apply for Leave');
  r.bullet('Log in to the Employee Portal > My Portal > Leave Requests.');
  r.bullet('Click "Apply Leave", select leave type, date range, and enter reason.');
  r.bullet('For planned leave, apply at least 3–5 working days in advance.');
  r.bullet('For emergencies, apply on the same day and also message admin via Chat.');
  r.bullet('Monitor status: Pending > Approved or Rejected.');
  r.br();
  r.infoBox('Taking leave without approval — even for one day — constitutes Unauthorised Absence and will be treated as Unpaid Leave. Repeated unauthorised absences are a disciplinary matter.');
  r.br();

  r.sectionTitle('4.3  Leave Types & Entitlements');
  r.br(2);

  // EL
  r.definition('Earned Leave (EL) — also called Privilege Leave (PL)',
    'Entitlement: 12 days per year (accrued monthly at 1 day per month). Maximum annual entitlement: 15 days. Usage: For planned rest, vacations, or extended personal commitments. Carry-Forward: Unused EL carries forward to the following year, subject to a maximum accumulated cap of 30 days. On Separation: Accumulated EL balance is encashed (paid out) as part of Full & Final Settlement. Legal Basis: U.P. Shops & Establishments Act.');
  r.hRule();

  // CL
  r.definition('Casual Leave (CL)',
    'Entitlement: 6 days per year (4 days maximum at a time per application). Usage: Unforeseen personal needs, urgent errands, short personal matters. Carry-Forward: Not permitted — unused CL lapses at year-end on 31 December. On Separation: Not encashed — lapses. Note: CL cannot be prefixed / suffixed to another type of leave to create an extended absence except with admin approval.');
  r.hRule();

  // SL
  r.definition('Sick Leave (SL)',
    'Entitlement: 6 days per year. Usage: Genuine illness or injury preventing work. Medical Certificate: Required for 3 or more consecutive sick leave days (valid certificate from a registered medical practitioner). Carry-Forward: Conditional — governed by applicable state rules, subject to a lifetime accumulated cap. On Separation: Not encashed — lapses. Legal Basis: Applicable state labour rules.');
  r.hRule();

  // ML
  r.definition('Maternity Leave (ML)',
    'Entitlement: 182 calendar days (26 weeks) of fully paid Maternity Leave. Eligibility: Female employees who have worked for at least 80 days in the 12-month period preceding the expected delivery date. Usage: Childbirth (before and after delivery), miscarriage, adoption (as applicable under the Act). Additional provisions: Post-maternity work-from-home arrangement may be considered on a case-by-case basis per the Act. Legal Basis: Maternity Benefit (Amendment) Act, 2017.');
  r.hRule();

  // PL
  r.definition('Paternity Leave (PL)',
    'Entitlement: Up to 5 days per birth / adoption event. Usage: For male employees around the birth or adoption of their child. Carry-Forward: Not applicable — does not carry forward. On Separation: Lapses. Subject to HR/admin approval.');
  r.hRule();

  // BL
  r.definition('Bereavement Leave (BL)',
    'Entitlement: Up to 3 days per bereavement event. Eligibility: On the death of an immediate family member. Immediate family includes: parents, parents-in-law, spouse, children, and siblings. Usage: Per event basis — not cumulative. Carry-Forward: Not applicable. On Separation: Lapses. Application: Apply immediately via portal and inform admin via Chat.');
  r.hRule();

  // UL
  r.definition('Unpaid Leave (UL)',
    'Entitlement: No balance required — available when all paid leave is exhausted. Salary Impact: A per-day salary deduction is applied for each UL day, reflecting in that month\'s payslip. Usage: Genuine situations when no paid leave balance remains. Use sparingly — requires admin approval. On Separation: No financial implication beyond the pay-period deductions already made.');
  r.br();

  r.sectionTitle('4.4  Leave Balance & Tracking');
  r.text('Your real-time leave balance (total entitlement, days taken, pending applications, and remaining balance) is visible in My Portal > Leave Requests > "Your Balances" tab. Balances are updated immediately on application or admin decision.', { size: 9.5, color: C.mid });
  r.br();

  r.sectionTitle('4.5  Half-Day Leave');
  r.text('Half-day leave is permitted. When applying, tick the "Half Day" checkbox and use the same date for start and end. A half day counts as 0.5 days against the relevant leave balance.', { size: 9.5, color: C.mid });
  r.br();

  r.sectionTitle('4.6  Carry-Forward Summary');
  const cfData = [
    ['Earned Leave (EL)', 'Yes', 'Max 30 days accumulated'],
    ['Casual Leave (CL)', 'No', 'Lapses at year-end'],
    ['Sick Leave (SL)', 'Conditional', 'State law cap applies'],
    ['Maternity Leave (ML)', 'N/A', '—'],
    ['Paternity Leave (PL)', 'No', 'Per event only'],
    ['Bereavement Leave (BL)', 'No', 'Per event only'],
    ['Unpaid Leave (UL)', 'N/A', 'No balance concept'],
  ];
  const doc = r.doc;
  const headers = ['Leave Type', 'Carry-Forward', 'Notes'];
  const colX = [MARGIN, MARGIN + 58, MARGIN + 100];
  // header row
  r.needsNewPage(12);
  doc.setFillColor(...C.brand);
  doc.rect(MARGIN, r.y, CW, 8, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(...C.white);
  headers.forEach((h, i) => doc.text(h, colX[i] + 3, r.y + 5.5));
  r.y += 8;
  cfData.forEach((row, idx) => {
    r.needsNewPage(8);
    if (idx % 2 === 0) {
      doc.setFillColor(...C.light);
      doc.rect(MARGIN, r.y, CW, 7.5, 'F');
    }
    doc.setFont('helvetica', idx === 0 ? 'bold' : 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...C.dark);
    doc.text(row[0], colX[0] + 3, r.y + 5);
    // colour the carry-forward value
    const cfColor = row[1] === 'Yes' ? C.green : row[1] === 'No' ? C.red : C.mid;
    doc.setTextColor(...cfColor);
    doc.setFont('helvetica', 'bold');
    doc.text(row[1], colX[1] + 3, r.y + 5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...C.mid);
    doc.text(row[2], colX[2] + 3, r.y + 5);
    r.y += 7.5;
  });
  r.br(4);

  r.sectionTitle('4.7  Leave on Separation');
  r.bullet('Earned Leave (EL): Accumulated balance is encashed in your Full & Final Settlement.');
  r.bullet('Casual Leave: Lapses — not encashed.');
  r.bullet('Sick Leave: Lapses — not encashed.');
  r.bullet('All other leave types: Lapse on separation.');
  r.legalNote('EL encashment on separation is a statutory right under the Uttar Pradesh Shops & Commercial Establishments Act. Non-payment of earned encashment entitlement is a violation of law.');
}

function ch5_Compensation(r) {
  r.chapterBanner(5, 'Compensation & Benefits').br(4);

  r.sectionTitle('5.1  CTC — What It Means');
  r.text('CTC (Cost to Company) is the total annual compensation committed by the company to an employee. It is not the same as take-home salary. CTC includes:', { size: 9.5, color: C.mid });
  r.bullet('Regular Monthly Gross Salary (paid every month)');
  r.bullet('Annual Variable Pay (performance-linked; released once in April)');
  r.bullet('Retention Bonus (tenure-linked; paid in 5 annual instalments)');
  r.bullet('Joining / Relocation Bonus (one-time, Year 1 only)');
  r.br();
  r.infoBox('Monthly take-home is typically less than 1/12th of CTC because several CTC components are paid annually, conditionally, or are non-cash benefits.');
  r.br();

  r.sectionTitle('5.2  Monthly Salary Components');
  r.text('Your monthly payslip will show the following Earnings components:', { size: 9.5, color: C.mid });
  r.br(2);
  r.definition('Basic Salary', 'The foundation of monthly pay. Used as base for HRA, PF, and gratuity calculations. Fully taxable.');
  r.definition('House Rent Allowance (HRA)', 'An allowance for housing expenses. Partially tax-exempt under Section 10(13A) of the Income Tax Act if you pay rent for your accommodation. Tax exemption requires submission of valid rent receipts. Consult a tax advisor for your specific situation.');
  r.definition('Leave Travel Allowance (LTA)', 'A monthly allowance towards domestic travel. Tax-exempt under Section 10(5) for actual domestic travel expenses for 2 journeys within a 4-year block period. Bills/tickets must be submitted to claim exemption. Any LTA received beyond actual travel cost is taxable.');
  r.definition('Medical Allowance', 'A fixed monthly allowance towards medical expenses. Fully taxable as salary in the current Indian tax framework.');
  r.definition('Telephone & Internet Allowance', 'A monthly allowance for official communication expenses. Exempt from tax on actual official bills submitted; any excess is taxable. Keep bills for official phone/internet usage.');
  r.definition('Special Allowance (Flexible Benefit Pay)', 'The residual component of monthly gross salary after all named allowances are accounted for. May be restructured under a Flexible Benefits Plan (FBP) where offered. Fully taxable as salary unless restructured under FBP.');
  r.br();

  r.sectionTitle('5.3  Additional Compensation Components');
  r.br(2);
  r.definition('Variable Pay (Annual Performance Bonus)',
    'Declared as part of CTC. Released once per year at financial year-end (typically in April), based on your annual performance review. It is not paid monthly and does not appear in regular payslips. Fully taxable under Section 15 of the Income Tax Act. TDS is deducted by the employer at the time of payment.');
  r.definition('Retention Bonus',
    'A tenure-linked bonus committed in the CTC from Day 1, where included in your Offer Letter. Paid in 5 annual equal instalments on completion of each year of service (end of Year 1, Year 2, Year 3, Year 4, Year 5). A separate Retention Bonus Commitment Letter is issued. Fully taxable. Applicable only where specified in your individual Offer Letter.');
  r.definition('Joining / Relocation Bonus',
    'A one-time cash payment in Year 1. Paid within 30–60 days of joining. Fully taxable. Potentially recoverable if the employee exits before completing 12 months of service — refer to your Offer Letter for exact recovery terms.');
  r.br();

  r.sectionTitle('5.4  Deductions');
  r.definition('Provident Fund (PF / EPF)', 'A statutory savings scheme under the Employees\' Provident Funds and Miscellaneous Provisions Act, 1952. A percentage of Basic Salary is deducted monthly and deposited in your EPF account. The employer also contributes. Accumulated PF is available on exit/retirement as per EPFO rules.');
  r.definition('Profession Tax (PT)',  'A state-level tax levied by the Government of Uttar Pradesh on salaried employees. Deducted monthly at applicable slab rates as per state rules. Fully deductible from taxable income under Section 16(iii) of the Income Tax Act.');
  r.definition('Income Tax / TDS',     'Tax Deducted at Source (TDS) on your salary under Section 192 of the Income Tax Act. The employer calculates projected annual tax liability and deducts TDS proportionately each month. Submitting your PAN card is mandatory — without it, TDS is deducted at a higher flat rate. Form 16 (TDS Certificate) is issued annually after March 31.');
  r.definition('ESIC', 'Applicable only where an employee\'s gross monthly salary is at or below the statutory wage ceiling (₹21,000/month) under the Employees\' State Insurance Act, 1948. Where applicable, both employer and employee contributions are made as per prevailing ESIC rates. Not applicable to employees whose gross monthly salary exceeds the ceiling — refer to your Offer Letter for your specific status.');
  r.br();

  r.sectionTitle('5.6  Health Insurance & Other Benefits');
  r.text('As of the date of this manual, the Company does not provide group health insurance / mediclaim coverage as a standard benefit, and it does not form part of CTC. Should this benefit be introduced in future, it will be communicated separately in writing and will not automatically alter any employee\'s agreed CTC.', { size: 9.5, color: C.mid });
  r.br();

  r.sectionTitle('5.5  Gratuity');
  r.text('Gratuity is a statutory benefit payable under the Payment of Gratuity Act, 1972. An employee becomes eligible after completing 5 continuous years of service. It is calculated on the basis of your Basic Salary and total years of service. Gratuity is paid at the time of separation (on retirement, resignation after 5 years, or termination).', { size: 9.5, color: C.mid });
  r.legalNote('Gratuity is an employee\'s right under law. Denial or non-payment of gratuity after 5 years of service is a statutory violation. Employees should retain all joining and employment documents to support any future gratuity claim.');
}

function ch6_Payroll(r) {
  r.chapterBanner(6, 'Payroll & Salary Administration').br(4);

  r.sectionTitle('6.1  Pay Cycle');
  r.text('Salary is processed monthly. The pay cycle is:', { size: 9.5, color: C.mid });
  r.bullet('Pay Period: 1st to last working day of the calendar month.');
  r.bullet('Credit Date: Between the 30th of the current month and the 5th of the following month.');
  r.bullet('Mode: Direct bank transfer to your registered salary account.');
  r.infoBox('If salary is not credited by the 5th of the following month, contact admin immediately via My Portal > Chat with Admin.');
  r.br();

  r.sectionTitle('6.2  Salary Slip');
  r.text('A detailed salary slip is generated each month and available under My Portal > My Salary / Payroll. It includes:', { size: 9.5, color: C.mid });
  r.bullet('Employee Name, ID, Department, Designation, and Pay Period');
  r.bullet('Full earnings breakdown (all allowance components)');
  r.bullet('All statutory and other deductions');
  r.bullet('Net Pay (take-home amount)');
  r.text('Salary slips can be downloaded as PDF at any time for tax filing, bank submissions, or personal records. Slips for all previous months are also available.', { size: 9.5, color: C.mid });
  r.br();

  r.sectionTitle('6.3  Unpaid Leave Deduction');
  r.text('For each Unpaid Leave (UL) day taken in a month, a per-day deduction is applied to your gross salary for that month. The deduction amount and number of UL days are clearly shown in the payslip under Deductions.', { size: 9.5, color: C.mid });
  r.br();

  r.sectionTitle('6.4  Tax & Form 16');
  r.text('TDS is deducted monthly from your salary as per your projected annual tax liability. At fiscal year-end (by 15 June each year as per law), you will receive Form 16 — the TDS Certificate summarising all tax deducted during the year. Form 16 is required to file your Annual Income Tax Return (ITR). Submit your PAN card to HR at the earliest to ensure correct TDS processing.', { size: 9.5, color: C.mid });
  r.br();

  r.sectionTitle('6.5  Salary Queries & Discrepancies');
  r.text('Report any salary discrepancy or query to admin via My Portal > Chat with Admin on the same day you notice it. State the month, the specific component in question, and the nature of the discrepancy. Corrections are typically processed in the next pay cycle.', { size: 9.5, color: C.mid });
  r.br();

  r.sectionTitle('6.6  Bank Account Details');
  r.text('Your salary is transferred to the bank account registered with HR. To update bank details, contact admin via Chat and provide your new account number, bank name, branch, IFSC code, and a cancelled cheque or passbook copy. Bank updates must be submitted at least 5 working days before the pay-cycle date to reflect in the current month.', { size: 9.5, color: C.mid });
}

function ch7_Performance(r) {
  r.chapterBanner(7, 'Performance Management').br(4);

  r.sectionTitle('7.1  Performance Review Cycle');
  r.text('Performance is evaluated on a quarterly basis (every ~4 months). The quarterly review is conducted by admin using the Performance & Appraisal tools in the Employee Portal. Results are communicated via the portal (My Portal > My Performance) and via Chat.', { size: 9.5, color: C.mid });
  r.br();

  r.sectionTitle('7.2  Key Result Areas (KRAs)');
  r.text('Your performance is assessed across the following Key Result Areas:', { size: 9.5, color: C.mid });
  r.bullet('Work Quality: Accuracy, depth, thoroughness, and market-standard quality of notes, lectures, and test questions.');
  r.bullet('Deadline Adherence: Completing assigned chapters and tasks on or before the agreed due date.');
  r.bullet('Punctuality: Consistent check-in at 9:30 AM and check-out at 6:30 PM with minimal attendance irregularities.');
  r.bullet('Professional Conduct: Communication quality, responsiveness, adherence to guidelines, and collaboration.');
  r.bullet('Initiative & Extra Contributions: Proactive work, extra hours logged, and above-minimum chapter output.');
  r.br();

  r.sectionTitle('7.3  KPIs and Scoring');
  r.text('Within each KRA, specific Key Performance Indicators (KPIs) are set and scored. KPI scores range from 0–100%. Your overall Performance KPI score is derived from these individual ratings. This score directly influences:', { size: 9.5, color: C.mid });
  r.bullet('Annual increment amount and percentage');
  r.bullet('Variable Pay release percentage');
  r.bullet('Mid-year additional increment eligibility');
  r.br();

  r.sectionTitle('7.4  Appraisal Process');
  r.text('An Appraisal is a structured salary and performance review conducted by admin. During appraisal:', { size: 9.5, color: C.mid });
  r.bullet('Admin reviews performance records, attendance, and task history for the appraisal period.');
  r.bullet('Performance KPI is set or updated in the portal.');
  r.bullet('New salary / CTC (if incremented) is calculated and applied.');
  r.bullet('Outcome — new salary structure, KPI score, and any increment — is communicated to the employee.');
  r.br();

  r.sectionTitle('7.5  Increments');
  r.text('Two types of increments may be granted:', { size: 9.5, color: C.mid });
  r.definition('Annual Increment', 'Eligible after completing one full year of service (12 months from joining date). Based on aggregate quarterly performance over the year. Effective from the next pay cycle after appraisal.');
  r.definition('Additional / Mid-Year Increment', 'May be granted mid-cycle for exceptional performance in a quarter. Not guaranteed — entirely at the discretion of management based on outstanding contribution.');
  r.br();

  r.sectionTitle('7.6  Variable Pay Release');
  r.text('Variable Pay (declared in CTC) is released once per year, typically in April. The actual amount released is proportional to your Performance KPI score for the year. Variable Pay is not guaranteed — it is earned through performance.', { size: 9.5, color: C.mid });
  r.br();

  r.sectionTitle('7.7  Performance Concerns & Improvement');
  r.text('If performance consistently falls below expectations:', { size: 9.5, color: C.mid });
  r.bullet('Admin will communicate specific concerns via Chat with factual examples.');
  r.bullet('A clear expectation-setting discussion will take place, with an improvement period agreed.');
  r.bullet('Continued underperformance may affect increment decisions, Variable Pay release, and employment continuation.');
  r.text('Employees who are facing challenges that affect their performance are strongly encouraged to raise these proactively with admin. Early communication always leads to better outcomes.', { size: 9.5, color: C.mid });
  r.br();

  r.sectionTitle('7.8  Reviewing Your Performance Record');
  r.text('You can view your performance metrics, KPI scores, and appraisal history at any time in My Portal > My Performance. If you have a concern or disagreement with a recorded rating, raise it via Chat with Admin with specific supporting examples.', { size: 9.5, color: C.mid });
}

function ch8_Documents(r) {
  r.chapterBanner(8, 'Employee Documents & Records').br(4);

  r.sectionTitle('8.1  Joining Documents Required');
  r.text('The following documents must be submitted to HR on or before your joining date:', { size: 9.5, color: C.mid });
  r.bullet('Government-issued ID Proof: Aadhaar Card (original + self-attested copy)');
  r.bullet('PAN Card (mandatory for TDS / income tax processing)');
  r.bullet('Education Certificates: degree, diploma, or marksheets relevant to your role');
  r.bullet('Bank Account Details: cancelled cheque or bank passbook copy (account number + IFSC)');
  r.bullet('Previous employment documents if applicable: last Offer Letter, experience letter, or relieving letter');
  r.bullet('Passport-size photograph (for employee profile)');
  r.br();
  r.legalNote('PAN is legally required for TDS processing under Section 206AA of the Income Tax Act. Without a valid PAN, TDS is deducted at a flat rate of 20%. Submit PAN at the earliest if not already done.');
  r.br();

  r.sectionTitle('8.2  UAN & EPF Registration');
  r.text('If Provident Fund (EPF) is applicable to your employment, your Universal Account Number (UAN) will be allotted or linked. The UAN is permanent throughout your career and portable across employers. HR will communicate EPF/UAN details in due course.', { size: 9.5, color: C.mid });
  r.br();

  r.sectionTitle('8.3  Document Storage on the Portal');
  r.text('Key employment documents are stored securely against your employee profile on the portal. The following categories are maintained:', { size: 9.5, color: C.mid });
  r.bullet('Offer Letter and Employment Contract');
  r.bullet('ID Proof copies');
  r.bullet('Education Certificates');
  r.bullet('Bank Documents');
  r.bullet('Other HR documents as required');
  r.br();

  r.sectionTitle('8.4  Accessing Your Documents');
  r.text('If you need to view, download, or verify any document uploaded against your profile, contact admin via My Portal > Chat with Admin. Specify the document type and the purpose of the request. Authorised personnel process document requests promptly.', { size: 9.5, color: C.mid });
  r.br();

  r.sectionTitle('8.5  Experience Letter');
  r.text('An official Experience Letter confirming your employment tenure, designation, and department is issued after your exit from the organisation once your leaving date is formally recorded. The letter can be generated as a downloadable PDF from the portal.', { size: 9.5, color: C.mid });
  r.br();

  r.sectionTitle('8.6  Employment Verification Letter');
  r.text('If you require a letter for bank loan, visa, or any third-party verification during active employment, contact admin via Chat. Specify the exact details needed (addressed to, purpose, information required). HR will review and issue an Employment Verification Letter or Salary Certificate as appropriate.', { size: 9.5, color: C.mid });
}

function ch9_Conduct(r) {
  r.chapterBanner(9, 'Code of Conduct & Professional Standards').br(4);

  r.sectionTitle('9.1  General Standards');
  r.text('All employees of PrrepX Pathshala are expected to maintain the highest standards of professional conduct at all times. The following principles define the expected standard:', { size: 9.5, color: C.mid });
  r.bullet('Honesty and Integrity: Act truthfully in all professional dealings. Do not misrepresent work progress, attendance, or any other information.');
  r.bullet('Respect: Treat all colleagues, management, and external stakeholders with dignity and respect. Discriminatory, offensive, or disrespectful behaviour is not tolerated.');
  r.bullet('Accountability: Own your responsibilities. If you make an error, acknowledge it promptly and take corrective action.');
  r.bullet('Professionalism: Maintain professional boundaries in all interactions on the portal, via chat, and in any external communication.');
  r.br();

  r.sectionTitle('9.2  Daily Working Standards');
  r.bullet('Check in at 9:30 AM and check out at 6:30 PM without fail.');
  r.bullet('Write a daily progress log on your active task before logging off each day.');
  r.bullet('Respond to admin messages on Chat within a reasonable time during working hours (no more than 2 hours for non-urgent messages).');
  r.bullet('Keep all Drive content organised by Subject > Chapter > Video/Notes as instructed.');
  r.bullet('Notify admin proactively if a task deadline cannot be met — do not wait until the deadline passes.');
  r.br();

  r.sectionTitle('9.3  Social Media & External Communication');
  r.bullet('Positive, professional mentions of PrrepX Pathshala are welcome.');
  r.bullet('Do not share internal content, student data, salary details, internal processes, or any confidential information on social media or publicly.');
  r.bullet('Do not post on behalf of or as a representative of the company without prior written authorisation from management.');
  r.bullet('Content you create on behalf of the company (notes, videos, test questions) is owned by PrrepX Pathshala and may not be shared or published externally by you.');
  r.br();

  r.sectionTitle('9.4  Dual Employment & Concurrent Freelancing');
  r.text('PrrepX Pathshala requires your undivided professional commitment throughout the term of your employment. Taking up any secondary employment, freelance engagement, or external commercial activity that competes with or materially conflicts with the Company\'s interests is strictly prohibited without prior express written approval from the Superadmin. This includes — but is not limited to — providing teaching, tutoring, or educational content services to any external entity. See Section 9.7 for detailed provisions specifically governing outside teaching and content creation activities.', { size: 9.5, color: C.mid });
  r.br();

  r.sectionTitle('9.5  Use of Company Resources');
  r.bullet('Company-provided accounts, platforms, Content Drive access, and portal access are for official use only.');
  r.bullet('Do not share credentials with any third party.');
  r.bullet('Report any security concern or suspected unauthorised access to admin immediately.');
  r.br();

  r.sectionTitle('9.6  Disciplinary Action');
  r.text('Violations of the Code of Conduct may result in disciplinary action proportional to the severity and frequency of the breach. The stages of disciplinary action are:', { size: 9.5, color: C.mid });
  r.bullet('Verbal Warning (documented in portal)');
  r.bullet('Written Warning (formal communication issued via Chat and/or email)');
  r.bullet('Suspension Pending Enquiry, where conduct is of a serious nature: a subsistence allowance of 50% of gross salary is paid for the first 90 days of suspension, and 75% thereafter if the inquiry extends beyond 90 days');
  r.bullet('Performance Improvement Plan (PIP) with specific targets and timelines');
  r.bullet('Termination of employment for serious or repeated breaches');
  r.br();
  r.legalNote('Termination on grounds of unsatisfactory performance following a PIP is treated separately from termination for misconduct ("cause") — see Chapter 13.1. It carries the standard notice period / payment in lieu of notice and does not trigger forfeiture of Retention Bonus instalments.');
  r.br();

  r.sectionTitle('9.7  Exclusive Service: Prohibition on Outside Teaching & Educational Content');
  r.text('As a teaching and educational content professional employed by PrrepX Pathshala, you are subject to a strict exclusive service obligation covering all teaching and educational activities. During the entire term of your employment, you must not — without prior express written approval from the Superadmin — engage in any of the following:', { size: 9.5, color: C.mid });
  r.bullet('Teach, tutor, coach, or mentor any student or group of students at any other coaching institute, school, college, or educational institution — whether offline or online, in-person or remote.');
  r.bullet('Deliver paid or unpaid lectures, demo sessions, workshops, courses, or webinars on any competing EdTech platform or online educational service provider, whether established or emerging.');
  r.bullet('Conduct private tuitions — individual or group — for school or competitive exam students, whether at a student\'s home, your own premises, a rented space, or via video call (Zoom, Google Meet, WhatsApp, etc.).');
  r.bullet('Create, record, upload, or publish monetised, sponsored, or otherwise commercially significant educational video or written content (YouTube, Instagram, Facebook, Telegram paid channels, etc.) covering subject areas relevant to your role.');
  r.bullet('Provide free or unpaid guest lectures, trial classes, or demo sessions at or for any other educational institution or competitor organisation.');
  r.bullet('Enter into any arrangement — formal or informal, paid or unpaid, verbal or written — with any other educational entity to provide teaching-related services.');
  r.br();
  r.infoBox('This restriction applies equally to paid and unpaid engagements. Even unpaid outside teaching creates a direct conflict of interest — it diverts your time, energy, and professional focus from your primary responsibilities, and risks inadvertent exposure of Company curricula, pedagogy, question banks, or student data to external parties.');
  r.br();
  r.text('Exception Procedure: If you wish to engage in any of the above activities for a legitimate, non-competing purpose, you must submit a written request to your reporting manager and the Superadmin describing: (a) the nature and subject matter of the activity, (b) the platform or institution involved, (c) the duration and frequency, and (d) the monetary consideration, if any. Approval may be granted or withheld at the Company\'s sole and absolute discretion.', { size: 9.5, color: C.mid });
  r.br();
  r.legalNote('Legal Basis — During-Employment Restriction: This policy is grounded in the implied duty of fidelity that attaches to every contract of employment under Indian common law, as affirmed by the Supreme Court of India in Niranjan Shankar Golikari v. Century Spinning & Mfg. Co. Ltd. (AIR 1967 SC 1098), wherein the Court held that negative covenants operative during the subsisting period of employment are valid and enforceable. Restrictions during active employment fall entirely outside the ambit of Section 27 of the Indian Contract Act, 1872 (which only voids agreements in restraint of trade operative after the termination of employment). This restriction also protects the Company\'s legitimate business interests — including its proprietary curriculum, student relationships, and teaching methodology — which are recognised as protectable interests under Indian contract and equity law.');
  r.br();
  r.text('Consequences of Violation: Breach of this policy constitutes a serious disciplinary offence and may result in one or more of the following:', { size: 9.5, color: C.mid });
  r.bullet('Immediate termination of employment without notice or notice pay');
  r.bullet('Forfeiture of any accrued retention bonus, performance bonus, or pending salary increments');
  r.bullet('Recovery of Company-incurred training, onboarding, or certification costs');
  r.bullet('Civil action for damages — including loss of students, misappropriation of curriculum, and breach of fiduciary duty — under applicable law');
  r.br();
  r.text('Non-Solicitation of Students After Separation: For a period of 12 (twelve) months following your last working day at PrrepX Pathshala, you must not directly or indirectly solicit, recruit, or invite any student who was enrolled on, interacting with, or a registered user of the PrrepX Pathshala platform during your tenure to join, subscribe to, or enrol with any competing educational service you are associated with. This post-employment obligation is narrowly tailored to protect the Company\'s student relationships and is enforceable as a legitimate business interest restriction distinct from a general restraint of trade.', { size: 9.5, color: C.mid });
  r.legalNote('Note: A blanket post-employment prohibition on teaching would be void under Section 27 of the Indian Contract Act, 1872 as an agreement in restraint of trade. Accordingly, this Manual does not impose such a broad restriction. The post-employment obligation is limited to non-solicitation of the Company\'s existing students — a targeted, proportionate measure that Indian courts have consistently recognised as enforceable for protecting legitimate business goodwill.');
}

function ch10_Confidentiality(r) {
  r.chapterBanner(10, 'Confidentiality & Intellectual Property').br(4);

  r.sectionTitle('10.1  Confidential Information');
  r.text('During your employment, you will have access to confidential information. The following categories are strictly confidential and must never be disclosed to any external party:', { size: 9.5, color: C.mid });
  r.bullet('All content created for the platform (notes, video lectures, test questions, solutions)');
  r.bullet('Student data, user profiles, exam results, and any personally identifiable information');
  r.bullet('Business strategies, pricing, platform features, and internal processes');
  r.bullet('Salary and compensation details of yourself and colleagues');
  r.bullet('Access credentials to all company systems and platforms');
  r.bullet('Internal communications, reports, and HR records');
  r.br();

  r.sectionTitle('10.2  Intellectual Property');
  r.text('All intellectual property created by you in the course of your employment — including notes, lecture scripts and recordings, test papers, presentation slides, software, and any other materials — is the sole property of PrrepX Pathshala Pvt. Ltd.', { size: 9.5, color: C.mid });
  r.br();
  r.legalNote('Under Sections 2, 17, and 40 of the Indian Copyright Act, 1957, works created during the course of employment under a contract of service belong to the employer. You have no publishing, reproduction, or distribution rights over such materials without written company consent.');
  r.br();

  r.sectionTitle('10.3  Obligations After Exit');
  r.text('Confidentiality obligations continue beyond your employment. Even after you leave the organisation, you must:', { size: 9.5, color: C.mid });
  r.bullet('Not disclose, reproduce, or commercially exploit any company-owned intellectual property.');
  r.bullet('Return or delete all company materials, credentials, and platform access in your possession.');
  r.bullet('Not solicit students, colleagues, or business partners for a competing enterprise for a period as specified in your Offer Letter or employment agreement.');
  r.bullet('For 12 months post-separation: Not directly or indirectly solicit or recruit any student who was enrolled on or associated with PrrepX Pathshala during your employment to join any competing educational service — see Section 9.7 (Non-Solicitation of Students After Separation).');
  r.bullet('Not use, reproduce, or distribute any proprietary teaching materials, question banks, test papers, lecture notes, or study content created during your employment — these remain the exclusive intellectual property of PrrepX Pathshala under Sections 2, 17, and 40 of the Indian Copyright Act, 1957.');
  r.br();

  r.sectionTitle('10.4  Notice on Collection of Your Personal Data (DPDP Act, 2023)');
  r.text('In accordance with Section 5 of the Digital Personal Data Protection Act, 2023, you are informed that the Company collects personal data (name, contact details, PAN, Aadhaar, bank details, educational/employment history, photographs, and related information) for the purposes of employment administration, payroll and statutory compliance, background verification, and workplace monitoring as described in this manual. You may raise any grievance regarding processing of your personal data in writing to HR. Detailed terms are set out in your Offer Letter and the Company\'s Privacy Policy.', { size: 9.5, color: C.mid });
}

function ch11_Grievance(r) {
  r.chapterBanner(11, 'Grievance Redressal Procedure').br(4);

  r.sectionTitle('11.1  Objective');
  r.text('PrrepX Pathshala is committed to providing a fair and transparent mechanism for all employees to raise workplace concerns, complaints, or grievances without fear of victimisation or retaliation.', { size: 9.5, color: C.mid });
  r.br();

  r.sectionTitle('11.2  What Constitutes a Grievance');
  r.bullet('Concerns about work conditions, task assignments, or workload');
  r.bullet('Concerns about unfair treatment, discrimination, or harassment');
  r.bullet('Salary discrepancies or payment disputes');
  r.bullet('Issues with performance ratings or appraisals');
  r.bullet('Any workplace situation causing distress or affecting work quality');
  r.br();

  r.sectionTitle('11.3  Grievance Raising Process');
  r.text('Step 1 — Informal Resolution: Raise the matter directly with admin via My Portal > Chat with Admin. Describe the issue clearly and factually. Admin will respond within 2 working days.', { size: 9.5, color: C.mid });
  r.br(2);
  r.text('Step 2 — Escalation: If the concern involves admin, or if the informal resolution is unsatisfactory, escalate to the Superadmin via Chat or direct communication. The Superadmin will review the matter impartially.', { size: 9.5, color: C.mid });
  r.br(2);
  r.text('Step 3 — Formal Record: For serious grievances, request a formal written acknowledgement via Chat. All records are maintained in the portal.', { size: 9.5, color: C.mid });
  r.br();

  r.sectionTitle('11.4  Anti-Retaliation');
  r.text('No employee will face any adverse action, victimisation, or disadvantage for raising a genuine grievance in good faith. Retaliation against an employee for raising a grievance is itself a disciplinary offence.', { size: 9.5, color: C.mid });
  r.br();

  r.sectionTitle('11.5  External Redressal');
  r.text('Employees retain the right to approach the appropriate Labour Authority, conciliation officer, or other statutory authority under applicable labour laws if an internal grievance remains unresolved.', { size: 9.5, color: C.mid });
  r.legalNote('The Industrial Disputes Act, 1947, and applicable state rules provide for conciliation, arbitration, and adjudication of labour disputes. Employees who have exhausted internal channels may invoke these rights without fear of reprisal. Any arbitration clause in the Offer Letter does not oust the exclusive statutory jurisdiction of Labour Courts / Industrial Tribunals for employees who qualify as "workmen" under applicable law.');
}

function ch12_AntiHarassment(r) {
  r.chapterBanner(12, 'Anti-Harassment & Safe Workplace Policy').br(4);

  r.sectionTitle('12.1  Policy Statement');
  r.text('PrrepX Pathshala maintains a zero-tolerance policy towards any form of harassment, bullying, intimidation, or discrimination in the workplace. Every employee has the right to a professional environment free from such conduct.', { size: 9.5, color: C.mid });
  r.br();

  r.sectionTitle('12.2  Prevention of Sexual Harassment (POSH)');
  r.text('The company is committed to compliance with the Sexual Harassment of Women at Workplace (Prevention, Prohibition and Redressal) Act, 2013. The following principles apply:', { size: 9.5, color: C.mid });
  r.bullet('Sexual harassment in any form — physical, verbal, written, or digital — is strictly prohibited.');
  r.bullet('A confidential complaint mechanism is available: report to Superadmin directly if you experience or witness any such conduct.');
  r.bullet('All complaints will be investigated promptly, impartially, and confidentially.');
  r.bullet('The company will take appropriate remedial and disciplinary action, up to and including termination.');
  r.legalNote('The Sexual Harassment of Women at Workplace (Prevention, Prohibition and Redressal) Act, 2013 — also known as the POSH Act — is a statutory right. Complaints may also be filed with the Local Complaints Committee (LCC) at the district level if not satisfactorily resolved internally.');
  r.br();

  r.sectionTitle('12.3  Other Forms of Prohibited Conduct');
  r.bullet('Bullying: Repeated aggressive, demeaning, or intimidating behaviour towards a colleague.');
  r.bullet('Discrimination: Treating any employee unfavourably on the basis of gender, religion, caste, nationality, disability, or any other protected characteristic.');
  r.bullet('Verbal Abuse: Use of offensive, humiliating, or threatening language in any communication.');
  r.bullet('Cyberbullying: Harassment or threatening behaviour through digital means (chat, social media, email, etc.).');
  r.br();

  r.sectionTitle('12.4  Reporting');
  r.text('To report an incident of harassment, bullying, or discrimination:', { size: 9.5, color: C.mid });
  r.bullet('Contact the Superadmin directly via the Employee Portal Chat or via phone (details provided on joining day).');
  r.bullet('Describe the incident with as much factual detail as possible (dates, nature of conduct, any witnesses).');
  r.bullet('Requests for confidentiality will be respected to the maximum extent possible while allowing the complaint to be investigated.');
  r.br();

  r.sectionTitle('12.5  False Complaints');
  r.text('While the company encourages genuine reporting, malicious or false complaints made with the intent to harm a colleague may also result in disciplinary action against the complainant.', { size: 9.5, color: C.mid });
}

function ch13_Separation(r) {
  r.chapterBanner(13, 'Separation & Exit Policy').br(4);

  r.sectionTitle('13.1  Types of Separation');
  r.definition('Resignation', 'Voluntary departure by the employee. The employee must notify admin via Chat and submit a formal written resignation before the last working day.');
  r.definition('Termination (by company)', 'Employment may be terminated by the company for serious misconduct or redundancy (with immediate effect in cases of gross misconduct), or for prolonged poor performance after a PIP (with standard notice / payment in lieu of notice — this is not treated as termination for "cause" and does not forfeit Retention Bonus instalments).');
  r.definition('End of Probation', 'In the rare case where employment is not confirmed post-probationary review, an appropriate advance notice will be given.');
  r.br();

  r.sectionTitle('13.2  Notice Period');
  r.text('You are required to provide advance notice of your intent to leave. The notice period is as specified in your Offer Letter. During the notice period:', { size: 9.5, color: C.mid });
  r.bullet('Regular salary continues as normal.');
  r.bullet('All current tasks must be handed over in a structured manner as directed by admin.');
  r.bullet('Content Drive content must be fully organised and up-to-date before the last day.');
  r.bullet('All company data, credentials, and materials must be returned or deleted.');
  r.br();

  r.sectionTitle('13.3  Full & Final (F&F) Settlement');
  r.text('F&F Settlement is processed after exit clearance is completed. It includes:', { size: 9.5, color: C.mid });
  r.bullet('Payment of final month\'s salary (pro-rated if applicable)');
  r.bullet('Encashment of accumulated Earned Leave (EL) balance');
  r.bullet('Any applicable Retention Bonus chunk if the year is completed');
  r.bullet('Recovery of any outstanding amounts (advances, bond obligations if applicable)');
  r.br();
  r.infoBox('F&F Settlement is processed only after all clearances are complete. Any delay in task handover, document return, or clearance sign-off may delay F&F processing accordingly.');
  r.br();

  r.sectionTitle('13.4  Experience Letter');
  r.text('After your leaving date is formally recorded and F&F is settled, an official Experience Letter confirming your employment tenure and designation is available for download via the portal. Raise a request via Chat if not received within 7 working days of exit.', { size: 9.5, color: C.mid });
  r.br();

  r.sectionTitle('13.5  Bond Recovery on Early Exit');
  r.text('If you exit before completing the minimum service period (2 years) specified in the bond clause of your Offer Letter, the company is entitled to recover the Bond Breach compensation (Training & Content Indemnity), calculated as 3 months\' Gross Monthly Salary. The payable amount is pro-rated: Maximum × (Months Remaining in Bond Period ÷ 24). Example: leaving after 1 year means 50% of maximum is due; after 1.5 years, 25%. This will be deducted from the F&F Settlement; any remaining balance is payable by the employee within a reasonable period as notified by HR.', { size: 9.5, color: C.mid });
  r.br();

  r.sectionTitle('13.6  Non-Solicitation & Post-Exit Obligations');
  r.text('For a period following your exit (as specified in your Offer Letter), you must not directly solicit current employees, students, or business partners of PrrepX Pathshala for the benefit of a competing organisation. Your obligations of confidentiality and intellectual property non-disclosure continue indefinitely after exit.', { size: 9.5, color: C.mid });
  r.br();

  r.infoBox('For all separation-related queries, contact admin via My Portal > Chat with Admin. All records of employment, attendance, and performance remain available in the portal during the notice period.');
}

function ch14_Holidays(r) {
  r.chapterBanner(14, 'Holidays & Observances').br(4);

  // Determine which year's table to use: match the current calendar year,
  // or fall back to the latest available year if no table exists yet.
  const calYear = new Date().getFullYear();
  const holidayRows = holidayTables[calYear] ?? holidayTables[Math.max(...Object.keys(holidayTables).map(Number))];
  const totalHolidayDays      = holidayRows.reduce((s, e) => s + e.count, 0);
  const totalHolidayOccasions = holidayRows.length;

  r.sectionTitle('14.1  Holiday Policy');
  r.text('PrrepX Pathshala observes paid public holidays each calendar year in line with Central Government Gazetted Holidays and applicable Uttar Pradesh State observances. On a declared holiday, attendance is not required and the day is treated as a fully paid non-working day.', { size: 9.5, color: C.mid });
  r.br();
  r.legalNote('The three National Holidays — Republic Day (26 Jan), Independence Day (15 Aug), and Gandhi Jayanti (2 Oct) — are treated by the Company as compulsory paid holidays for all establishments, in line with prevailing national practice and the applicable State Shops & Commercial Establishments Act. Employees are entitled to these three as a statutory minimum. All additional holidays listed below are company policy over and above the statutory floor.');
  r.br();

  r.sectionTitle(`14.2  Official Holiday Calendar -- ${calYear}`);
  r.text(`The following ${totalHolidayOccasions} holiday occasions (totalling ${totalHolidayDays} paid holiday days) are observed by PrrepX Pathshala for ${calYear}. Multi-day festivals show the full date range and exact day count. Holidays marked (*) are subject to moon sighting or final Gazette notification and may shift by +/- 1 day.`, { size: 9.5, color: C.mid });
  r.br(3);

  const doc = r.doc;

  // Each row from holidays.js is: { period, days, name, count, category, type }

  // 5 columns: Period(32) + Day(s)(22) + Holiday(68) + Days(12) + Category(40) = 174 = CW
  const colX = [MARGIN, MARGIN + 32, MARGIN + 54, MARGIN + 122, MARGIN + 134];
  const colW = [32, 22, 68, 12, 40];
  const heads = ['Date / Period', 'Day(s)', 'Holiday', 'Days', 'Category'];

  // --- Header row ---
  r.needsNewPage(10);
  doc.setFillColor(...C.brand);
  doc.rect(MARGIN, r.y, CW, 8, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(...C.white);
  heads.forEach((h, i) => {
    if (i === 3) {
      // 'Days' header -- centred in its narrow column
      doc.text(h, colX[i] + colW[i] / 2, r.y + 5.5, { align: 'center' });
    } else {
      doc.text(h, colX[i] + 2, r.y + 5.5);
    }
  });
  r.y += 8;

  // --- Data rows ---
  holidayRows.forEach((row, idx) => {
    const { period, days: dayStr, name, count: daysCount, category: cat, type } = row;
    const isMoon   = type === 'Moon' || type === 'Lunar';
    const isComp   = type === 'Fixed3';
    const isMulti  = daysCount > 1;

    // Pre-split holiday name to determine dynamic row height
    const nameLines = doc.splitTextToSize(name, colW[2] - 4);
    const rowH = Math.max(8, nameLines.length * 5 + 3);

    r.needsNewPage(rowH + 2);

    // Alternating row background
    if (idx % 2 === 0) {
      doc.setFillColor(...C.light);
      doc.rect(MARGIN, r.y, CW, rowH, 'F');
    }

    // Accent bar on left edge for multi-day holidays
    if (isMulti) {
      doc.setFillColor(...C.brand);
      doc.rect(MARGIN, r.y, 2, rowH, 'F');
    }

    const textY = r.y + rowH / 2 + 1.5; // vertically centred baseline

    // Col 0: Date / Period -- bold brand, larger for multi-day
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(isMulti ? 8.5 : 8.5);
    doc.setTextColor(...C.brand);
    doc.text(period, colX[0] + (isMulti ? 3 : 2), textY);

    // Col 1: Day(s)
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...C.mid);
    doc.text(dayStr, colX[1] + 2, textY);

    // Col 2: Holiday name (purple=lunar, green=compulsory, dark=regular)
    const nameColor = isMoon ? [107, 33, 168] : (isComp ? C.green : C.dark);
    doc.setFont(isMulti ? 'helvetica' : 'helvetica', isMulti ? 'bold' : 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...nameColor);
    // Vertically centre multi-line text block
    const firstLineY = r.y + (rowH - (nameLines.length - 1) * 4.5) / 2 + 1.5;
    nameLines.forEach((line, li) => {
      doc.text(line, colX[2] + 2, firstLineY + li * 4.5);
    });

    // Col 3: Days count -- centred, bold+brand for multi-day, grey for single
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...(isMulti ? C.brand : C.muted));
    doc.text(String(daysCount), colX[3] + colW[3] / 2, textY, { align: 'center' });

    // Col 4: Category
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...(isComp ? C.green : C.mid));
    doc.text(cat, colX[4] + 2, textY);

    r.y += rowH;
  });

  // --- Footer / total row ---
  r.needsNewPage(8);
  doc.setFillColor(...C.bannerBg);
  doc.rect(MARGIN, r.y, CW, 7.5, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(...C.brand);
  doc.text(
    `Total: ${totalHolidayDays} paid holiday days across ${totalHolidayOccasions} occasions  (01 Jan ${calYear} -- 31 Dec ${calYear})`,
    MARGIN + CW / 2, r.y + 5, { align: 'center' }
  );
  r.y += 7.5;
  r.br(4);

  // --- Legend ---
  r.needsNewPage(22);
  doc.setFillColor(249, 250, 251);
  doc.setDrawColor(...C.bannerBg);
  doc.setLineWidth(0.4);
  doc.roundedRect(MARGIN, r.y, CW, 22, 2, 2, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(...C.brand);
  doc.text('Legend:', MARGIN + 5, r.y + 7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...C.green);
  doc.text('Green text = Compulsory national holiday (Republic Day, Independence Day, Gandhi Jayanti)', MARGIN + 5, r.y + 13);
  doc.setTextColor(107, 33, 168);
  doc.text('Purple text (*) = Date subject to moon sighting / lunar calendar -- confirmed via official Gazette notification.', MARGIN + 5, r.y + 19);
  r.y += 28;
  r.br();

  r.sectionTitle('14.3  Festival Observance Notes');
  r.text('Holi is observed as a single paid holiday on Rangwali Holi (the colour festival / Dhulandi), which is the day gazetted by the Central and UP State Government. Holika Dahan (the bonfire eve) is a Restricted Holiday at Central level and is not a declared company holiday. Employees wishing to observe Holika Dahan may apply against their Casual Leave quota.', { size: 9.5, color: C.mid });
  r.br(2);
  r.text('Diwali is observed as a single paid holiday on the main day of Lakshmi Puja, as gazetted by the Central Government. Chhoti Diwali (Naraka Chaturdashi) and Govardhan Puja are Restricted / Optional holidays and are not declared company holidays. Bhai Dooj is a personal celebration and is not a company holiday. Employees may apply Casual Leave for any of these days.', { size: 9.5, color: C.mid });
  r.br(2);
  r.legalNote('Note for 2026: Diwali (Lakshmi Puja) falls on Sunday 08 November, which is the regular weekly off. Since no additional paid working day is lost, employees are entitled to observe the gazetted holiday on that day. HR may announce a substitute working-day observance if directed by management.');
  r.br();

  r.sectionTitle('14.4  Independence Day Note');
  r.text(`Independence Day ${calYear} falls on Saturday 15 August. As Saturday is a regular working day for PrrepX Pathshala, this holiday is fully observed -- no attendance is required and salary is not affected.`, { size: 9.5, color: C.mid });
  r.br();

  r.sectionTitle('14.5  Moon-Sighting Dependent Holidays');
  r.text('The dates for Eid-ul-Fitr (Shawwal 1), Eid-ul-Adha (Dhul Hijjah 10), Dussehra, Janmashtami, and Diwali are determined annually by the Hindu/Islamic lunar calendar and official Gazette notification. The dates in this calendar are best-available estimates as of print. HR will issue a written confirmation at least 48 hours before each such holiday once the Gazette notification is received.', { size: 9.5, color: C.mid });
  r.br();

  r.sectionTitle('14.6  Working on a Holiday');
  r.text('If operational requirements make it necessary for an employee to work on a declared holiday, the employee is entitled to a Compensatory Off (Comp-Off) on a mutually agreed working day within 30 days. The comp-off date must be confirmed with admin in advance via Chat. Monetary payment in lieu of comp-off is not provided.', { size: 9.5, color: C.mid });
  r.br();

  r.sectionTitle('14.7  Additional / Special Holidays');
  r.text('Management may declare ad-hoc holidays on special occasions (natural disasters, national events, etc.). Such holidays will be communicated via the Employee Portal notification system with as much advance notice as possible. These do not form part of the 17-day annual entitlement listed above.', { size: 9.5, color: C.mid });
}

function ch15_ITPolicy(r) {
  r.chapterBanner(15, 'IT, Data & Digital Work Policy').br(4);

  r.sectionTitle('15.1  Purpose');
  r.text('As an EdTech company, nearly all work at PrrepX Pathshala involves digital systems -- the Employee Portal, Google Drive, email, video recording tools, and online platforms. This chapter sets out the rules for responsible, secure, and professional use of all company digital systems.', { size: 9.5, color: C.mid });
  r.br();

  r.sectionTitle('15.2  Company Accounts & Access');
  r.bullet('All accounts provided by the company (Google Workspace, Employee Portal, Drive, etc.) are company property. They must be used solely for official work.');
  r.bullet('Credentials must not be shared with any person -- including family members, friends, or other employees who do not require that access.');
  r.bullet('If you suspect your credentials have been compromised, report it to admin immediately via Chat.');
  r.bullet('On separation, all company accounts are deactivated. You must not retain copies of company data after exit.');
  r.br();

  r.sectionTitle('15.3  Google Drive -- Content Organisation');
  r.text('All educational content (notes, slides, and recorded videos) is stored in a Dedicated Drive Link shared by admin on your joining day. The mandatory organisation structure is:', { size: 9.5, color: C.mid });
  r.br(2);
  r.bullet('Top level: Subject name (e.g., Physics, Chemistry, Biology)');
  r.bullet('Sub-level: Chapter name (e.g., Chapter 1 - Motion in a Straight Line)');
  r.bullet('Inside each chapter folder: Notes PDF, Slides, and Video files (numbered in sequence)');
  r.br();
  r.infoBox('Disorganised Drive content is a performance issue. Admin reviews Drive structure during chapter quality checks. Always follow the naming convention briefed by your admin on joining day.');
  r.br();

  r.sectionTitle('15.4  Video Recording Standards');
  r.bullet('Record in a quiet environment with no background noise.');
  r.bullet('Use clear, stable camera framing -- face and board/screen both visible.');
  r.bullet('Minimum resolution: 720p (1080p preferred). Check recording quality before uploading.');
  r.bullet('File naming: [Subject]_Ch[XX]_V[YY]_[TopicName] (e.g., Physics_Ch01_V03_Newtons_Laws)');
  r.bullet('Notify admin via Chat after every video upload -- do not wait until the full chapter is ready.');
  r.br();

  r.sectionTitle('15.5  Employee Portal Usage');
  r.text('The Employee Portal is the primary system for all HR functions -- attendance, tasks, leave, salary slips, and chat. You are expected to:', { size: 9.5, color: C.mid });
  r.bullet('Log in and mark attendance every working day without fail.');
  r.bullet('Keep your task status up to date and write a daily progress note before logging off.');
  r.bullet('Check notifications and respond to admin messages within 2 hours during working hours.');
  r.bullet('Use your own login only -- never allow someone else to mark attendance or update tasks on your behalf.');
  r.br();

  r.sectionTitle('15.6  Data Privacy & Student Data');
  r.text('PrrepX Pathshala collects and stores student data including names, contact details, purchase records, and test scores. All employees who access this data must:', { size: 9.5, color: C.mid });
  r.bullet('Access student data only when required for official work.');
  r.bullet('Never share student personal information with any external party.');
  r.bullet('Never use student contact details for any personal purpose.');
  r.legalNote('Student data is protected under applicable Indian data protection rules and the Information Technology Act, 2000, and its amendments. Unauthorised disclosure or misuse of student data is a criminal offence in addition to being a disciplinary matter.');
  r.br();

  r.sectionTitle('15.7  Prohibited Digital Activities');
  r.text('The following are strictly prohibited while using company accounts, systems, or during working hours:', { size: 9.5, color: C.mid });
  r.bullet('Uploading, sharing, or storing any form of illegal, offensive, or adult content.');
  r.bullet('Installing unauthorised software or browser extensions on company-linked systems.');
  r.bullet('Using company accounts to conduct personal business, freelance work, or any competing activity.');
  r.bullet('Accessing or attempting to access systems, accounts, or data beyond your authorised scope.');
  r.bullet('Using AI tools to generate content for the company platforms without explicit admin approval -- all content must be original and alumni-crafted.');
  r.br();

  r.sectionTitle('15.8  Device & Equipment Care');
  r.text('If the company provides any physical equipment (recording devices, tablets, etc.), you are responsible for its care and safe return. Loss or damage due to negligence may result in recovery of repair/replacement cost from salary. Report any equipment issue to admin immediately.', { size: 9.5, color: C.mid });
  r.br();

  r.sectionTitle('15.9  Reporting IT or Security Issues');
  r.text('Report any of the following to admin immediately via Chat:', { size: 9.5, color: C.mid });
  r.bullet('Suspected unauthorised access to your account or company data');
  r.bullet('Accidental deletion of content from shared Drive');
  r.bullet('Any phishing, suspicious email, or social engineering attempt');
  r.bullet('System downtime or access issues affecting your work');
  r.infoBox('Prompt reporting of IT issues prevents data loss and security breaches. You will not be penalised for honestly reporting an accidental issue -- silence is far more damaging than transparency.');
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN EXPORT
// ─────────────────────────────────────────────────────────────────────────────
export function generateHRManual() {
  const doc  = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const r    = new Renderer(doc);

  // ── Page 1: Cover ────────────────────────────────────────────────────────
  drawCover(doc);

  // ── Page 2: TOC placeholder (will be filled after all chapters) ──────────
  doc.addPage();
  r.page = 2;
  // draw page footer for page 2
  r._drawPageFooter();
  // (TOC content written later via drawTOC after chapters are done)

  // ── Pages 3+: Chapters ───────────────────────────────────────────────────
  doc.addPage();
  r.page = 3;
  r._drawPageHeader();

  ch1_About(r);
  r.addPage(); ch2_Employment(r);
  r.addPage(); ch3_Attendance(r);
  r.addPage(); ch4_Leave(r);
  r.addPage(); ch5_Compensation(r);
  r.addPage(); ch6_Payroll(r);
  r.addPage(); ch7_Performance(r);
  r.addPage(); ch8_Documents(r);
  r.addPage(); ch9_Conduct(r);
  r.addPage(); ch10_Confidentiality(r);
  r.addPage(); ch11_Grievance(r);
  r.addPage(); ch12_AntiHarassment(r);
  r.addPage(); ch13_Separation(r);
  r.addPage(); ch14_Holidays(r);
  r.addPage(); ch15_ITPolicy(r);

  // ── Back to TOC page ─────────────────────────────────────────────────────
  drawTOC(doc, r.toc, 2);

  // ── Save ─────────────────────────────────────────────────────────────────
  const today = new Date().toISOString().slice(0, 10);
  doc.save(`PrrepX_Pathshala_HR_Policy_Manual_${today}.pdf`);
}
